import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import './AlumniPage.scss';
// Import AlumCard component to display individual alumni
import AlumCard from '../BrotherCard/AlumCard';
import Reveal from '../Reveal/Reveal';

// Full pledge-class progression, oldest to newest. Classes with no alumni on
// record simply render nothing.
const PLEDGE_CLASS_ORDER = [
  'Founding', 'Alpha', 'Beta', 'Gamma', 'Delta', 'Epsilon', 'Zeta', 'Eta',
  'Theta', 'Iota', 'Kappa', 'Lambda', 'Mu', 'Nu', 'Xi', 'Omicron', 'Pi',
  'Rho', 'Sigma', 'Tau', 'Upsilon', 'Phi', 'Chi', 'Psi', 'Omega',
];

// Guards against blank/placeholder records without ever touching the source data.
function hasIdentity(brother) {
  return Boolean(brother && brother.name && brother.name.trim().length > 0);
}

function lastNameKey(brother) {
  const parts = (brother.name || '').trim().split(/\s+/);
  return (parts.length > 1 ? parts[parts.length - 1] : parts[0] || '').toLowerCase();
}

function byLastName(a, b) {
  return lastNameKey(a).localeCompare(lastNameKey(b));
}

// Caps the per-card reveal stagger so a long class list never takes more
// than ~360ms (6 steps * 60ms) to finish revealing.
const MAX_REVEAL_INDEX = 6;
function revealIndex(i) {
  return Math.min(i, MAX_REVEAL_INDEX);
}

// Turns a pledge-class name into its `?view=` query value, e.g. 'Theta' -> 'theta'.
function classViewKey(className) {
  return className.toLowerCase();
}

// Resolves the requested `?view=` value against the class filters that
// actually exist. Anything unrecognized (missing, stale, mistyped) safely
// falls back to 'all' rather than rendering an empty page.
function resolveView(requestedView, availableKeys) {
  return availableKeys.includes(requestedView) ? requestedView : 'all';
}

// Component that displays all alumni organized by their initiation class
function BrothersPage(props) {
  const brothers = (props.brothers || []).filter(hasIdentity);
  const location = useLocation();
  const history = useHistory();

  const classSections = PLEDGE_CLASS_ORDER
    .map((className) => ({
      className,
      members: brothers.filter((brother) => brother.class === className).sort(byLastName),
    }))
    .filter((section) => section.members.length > 0);

  const filters = [
    { key: 'all', label: 'All members' },
    ...classSections.map(({ className }) => ({ key: classViewKey(className), label: className })),
  ];

  const requestedView = new URLSearchParams(location.search).get('view') || 'all';
  const activeView = resolveView(requestedView, filters.map((filter) => filter.key));

  function selectView(key) {
    const search = key === 'all' ? '' : `?view=${key}`;
    history.push({ pathname: location.pathname, search });
  }

  const visibleClassSections =
    activeView === 'all'
      ? classSections
      : classSections.filter(({ className }) => classViewKey(className) === activeView);

  return (
    <div className="AlumniPage">
      {/* Hero section with background image and title */}
      <div className="alumni-hero">
        <div className="top-heading">
          <h1>Alumni</h1>
          <p className="hero-subtitle">Meet the Theta Tau Alumni</p>
        </div>
      </div>

      <div
        className="AlumniFilterGroup"
        role="group"
        aria-label="Filter alumni by class"
      >
        {filters.map(({ key, label }) => (
          <button
            type="button"
            key={key}
            className="AlumniFilterGroup-button"
            aria-pressed={activeView === key}
            onClick={() => selectView(key)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="ArchiveContainer">
        {visibleClassSections.map(({ className, members }) => (
          <section
            className="ArchiveSection"
            aria-labelledby={`${className}-class-heading`}
            key={className}
          >
            <h2 id={`${className}-class-heading`} className="ArchiveSection-title">
              {className} Class Alumni
            </h2>
            <div className="ArchiveGrid">
              {members.map((brother, i) => (
                <Reveal as="div" variant="rise" index={revealIndex(i)} key={brother.id}>
                  <AlumCard
                    name={brother.name}
                    major={brother.major && brother.major[0]}
                    headshot={brother.headshot}
                  />
                </Reveal>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default BrothersPage;
